import type { AuthTokens } from '../api/types';

/**
 * The signed-in session as persisted in localStorage. Tokens live in the
 * browser because the API is bearer-token only (no cookie session); the
 * trade-off is documented in docs/plans/2026-09-08-web-auth-dashboard.md.
 *
 * There is no ID token: the identity provider issues none, and everything the
 * app used to read from it comes from the sign-in response or from GET
 * /auth/me.
 *
 * Nor is there any expiry arithmetic here. An access token lives five
 * minutes, and the API renews it server-side: a response carrying
 * `X-Access-Token` brings its replacement, which the client stores. The
 * client's whole job is to send the token it has, swap it when that header
 * appears, and go to the sign-in page on a 401
 * (docs/plans/2026-09-23-cognito-to-stytch.md §5.2).
 */
export interface StoredSession {
  username: string;
  accessToken: string;
  refreshToken: string | null;
  /**
   * Epoch ms when the session ends, whatever the refresh token's own
   * validity (the API's sessionExpiresAt). Read for the "Session ends 14:00"
   * line, not to decide when to refresh. Null if the API did not report it.
   */
  sessionExpiresAt: number | null;
}

export const SESSION_KEY = 'balsam-hub.session';

/** Why a session was cleared, when the sign-in page should say so. */
export type SessionEndReason = 'session-expired';

type Listener = (session: StoredSession | null, reason?: SessionEndReason) => void;
const listeners = new Set<Listener>();

export function readSession(): StoredSession | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!isStoredSession(parsed)) return null;
    return { ...parsed, sessionExpiresAt: parsed.sessionExpiresAt ?? null };
  } catch {
    return null;
  }
}

export function writeSession(session: StoredSession): void {
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  } catch {
    // Storage can be unavailable (private mode, quota); the in-memory flow still works for this page.
  }
  notify(session);
}

/**
 * Replaces the stored access token with the one the API just renewed. The
 * token that was sent is dead, so this is the only copy; everything else
 * about the session is untouched, and nothing happens when there is no
 * session to update (a signed-out tab).
 */
export function storeRenewedAccessToken(accessToken: string): void {
  const session = readSession();
  if (!session || session.accessToken === accessToken) return;
  writeSession({ ...session, accessToken });
}

export function clearSession(reason?: SessionEndReason): void {
  try {
    localStorage.removeItem(SESSION_KEY);
  } catch {
    // ignore
  }
  notify(null, reason);
}

/** Fires on every write/clear in this tab and on changes made by other tabs. */
export function subscribeSession(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Builds the stored form from a login / challenge / refresh response. */
export function sessionFromTokens(
  tokens: AuthTokens,
  previous?: StoredSession | null,
): StoredSession {
  const reported = tokens.sessionExpiresAt ? Date.parse(tokens.sessionExpiresAt) : NaN;
  return {
    username: tokens.username,
    accessToken: tokens.accessToken,
    refreshToken: tokens.refreshToken ?? previous?.refreshToken ?? null,
    sessionExpiresAt: Number.isFinite(reported) ? reported : (previous?.sessionExpiresAt ?? null),
  };
}

/** True once the session has ended: nothing is attempted past this. */
export function isSessionOver(session: StoredSession): boolean {
  return session.sessionExpiresAt !== null && Date.now() >= session.sessionExpiresAt;
}

function isStoredSession(value: unknown): value is StoredSession {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.username === 'string' &&
    typeof v.accessToken === 'string' &&
    (v.refreshToken === null || typeof v.refreshToken === 'string') &&
    (v.sessionExpiresAt === undefined ||
      v.sessionExpiresAt === null ||
      typeof v.sessionExpiresAt === 'number')
  );
}

function notify(session: StoredSession | null, reason?: SessionEndReason): void {
  for (const listener of listeners) listener(session, reason);
}

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key === SESSION_KEY || event.key === null) notify(readSession());
  });
}
